import { ChangeEvent, useCallback } from 'react';

import { Input, SelectV2, Text } from '@pukka/ui-components';
import { useSnapshot } from 'valtio';

import { FormInput } from './FormInput';
import { IndividualFormState, onChangeValue, ValidationState } from './GopayActivationForm.state';

interface IndividualDetailFormProps {
  readonly IndividualState?: typeof IndividualFormState;
  readonly Validation?: typeof ValidationState;
}

function IndividualDetailForm({ IndividualState = IndividualFormState, Validation = ValidationState }: IndividualDetailFormProps) {
  const { dirty, id, nameById, addressById, citizenship, npwp, nameByNpwp, addressByNpwp, cob, dob, gender, job, permitNumber } =
    useSnapshot(IndividualState);
  const { individualValidation } = useSnapshot(Validation);

  const onChangeDate = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    onChangeValue('individual', 'dob')(e.target.value);
  }, []);

  return (
    <div>
      <Text as="h3" variant="title-2" weight="bold" className="mb-4" color="blue">
        Data Individual Merchant
      </Text>
      <FormInput
        required
        label="Nama PIC Merchant di KTP/KITAS (Khusus WNA)"
        description="Isi sesuai dengan yang tertulis di KTP/KITAS."
        error={dirty.has('nameById') && !individualValidation.nameById.success && individualValidation.nameById.error}
      >
        <Input
          type="text"
          value={nameById}
          onChangeValue={onChangeValue('individual', 'nameById')}
          placeholder="Nama lengkap"
          maxLength={100}
        />
      </FormInput>
      <FormInput
        required
        label="Nomor KTP PIC Merchant"
        description="Isi sesuai dengan yang tertulis di KTP."
        error={dirty.has('id') && !individualValidation.id.success && individualValidation.id.error}
      >
        <Input type="tel" value={id} onChangeValue={onChangeValue('individual', 'id')} placeholder="Nomor KTP" maxLength={16} />
      </FormInput>
      <FormInput
        label="Permit Number"
        description="Khusus untuk WNA (Warga Negara Asing)."
        error={dirty.has('permitNumber') && !individualValidation.permitNumber.success && individualValidation.permitNumber.error}
      >
        <Input
          type="text"
          value={permitNumber}
          onChangeValue={onChangeValue('individual', 'permitNumber')}
          placeholder="Permit number"
          maxLength={30}
        />
      </FormInput>
      <FormInput
        required
        label="Alamat sesuai KTP/KITAS"
        error={dirty.has('addressById') && !individualValidation.addressById.success && individualValidation.addressById.error}
      >
        <Input
          type="text"
          value={addressById}
          onChangeValue={onChangeValue('individual', 'addressById')}
          placeholder="Alamat lengkap"
          maxLength={250}
        />
      </FormInput>
      <FormInput
        required
        label="Kewarganegaraan"
        description="Isi sesuai dengan yang tertulis di KTP."
        error={dirty.has('citizenship') && !individualValidation.citizenship.success && individualValidation.citizenship.error}
      >
        <SelectV2
          placeholder="Kewarganegaraan"
          value={citizenship}
          onChange={onChangeValue('individual', 'citizenship') as (value: string | number | boolean) => void}
        >
          <SelectV2.Option value="WNI">WNI (Warga Negara Indonesia)</SelectV2.Option>
          <SelectV2.Option value="WNA">WNA (Warga Negara Asing)</SelectV2.Option>
        </SelectV2>
      </FormInput>
      <FormInput
        required
        label="Kota Kelahiran"
        description="Isi sesuai dengan yang tertulis di KTP."
        error={dirty.has('cob') && !individualValidation.cob.success && individualValidation.cob.error}
      >
        <Input type="text" value={cob} onChangeValue={onChangeValue('individual', 'cob')} placeholder="Kota kelahiran" maxLength={50} />
      </FormInput>
      <FormInput
        required
        label="Tanggal Lahir"
        error={dirty.has('dob') && !individualValidation.dob.success && individualValidation.dob.error}
      >
        <input
          type="date"
          id="dob"
          name="dob"
          data-testid="dob"
          className="rounded-md border-neutral-300"
          value={dob}
          onChange={onChangeDate}
        />
      </FormInput>
      <FormInput
        required
        label="Jenis Kelamin"
        description="Isi sesuai dengan yang tertulis di KTP."
        error={dirty.has('gender') && !individualValidation.gender.success && individualValidation.gender.error}
      >
        <SelectV2
          placeholder="Jenis kelamin"
          value={gender}
          onChange={onChangeValue('individual', 'gender') as (value: string | number | boolean) => void}
        >
          <SelectV2.Option value="Pria">Pria</SelectV2.Option>
          <SelectV2.Option value="Wanita">Wanita</SelectV2.Option>
        </SelectV2>
      </FormInput>
      <FormInput required label="Pekerjaan" error={dirty.has('job') && !individualValidation.job.success && individualValidation.job.error}>
        <Input type="text" value={job} onChangeValue={onChangeValue('individual', 'job')} placeholder="Pekerjaan" />
      </FormInput>
      <FormInput
        label="Nama di NPWP (opsional)"
        description="Isi sesuai dengan yang tertulis di NPWP. Jika tidak memiliki NPWP, silahkan isi dengan nama sesuai KTP."
        error={dirty.has('nameByNpwp') && !individualValidation.nameByNpwp.success && individualValidation.nameByNpwp.error}
      >
        <Input
          type="text"
          value={nameByNpwp}
          onChangeValue={onChangeValue('individual', 'nameByNpwp')}
          placeholder="Nama NPWP"
          maxLength={100}
        />
      </FormInput>
      <FormInput
        label="Nomor NPWP"
        description="Isi sesuai dengan yang tertulis di NPWP. Jika tidak memiliki NPWP, silahkan isi dengan nomor sesuai KTP."
        error={dirty.has('npwp') && !individualValidation.npwp.success && individualValidation.npwp.error}
      >
        <Input type="tel" value={npwp} onChangeValue={onChangeValue('individual', 'npwp')} placeholder="Nomor NPWP" maxLength={20} />
      </FormInput>
      <FormInput
        label="Alamat sesuai NPWP"
        description="Isi sesuai dengan yang tertulis di NPWP. Jika tidak memiliki NPWP, silahkan isi dengan nama sesuai KTP."
        error={dirty.has('addressByNpwp') && !individualValidation.addressByNpwp.success && individualValidation.addressByNpwp.error}
      >
        <Input
          type="text"
          value={addressByNpwp}
          onChangeValue={onChangeValue('individual', 'addressByNpwp')}
          placeholder="Alamat sesuai NPWP"
          maxLength={250}
        />
      </FormInput>
    </div>
  );
}

export { IndividualDetailForm };
